import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, resolveAsset, authHeader } from '../../api/client';
import { useT } from '../../i18n';

// Series management page — the workspace for a series. Each kid is an editable
// card (name / gender / age / photo / reference / price) that you fill in and
// then "create the book" for, one at a time: a live status pill, links to edit
// scenes / view the PDF, and an approve toggle (approval reuses the customer
// confirm semantics and is what makes the book count in stats). You can also add
// more kids to the series or remove a slot.

function StatusPill({ status }) {
  const t = useT();
  const STATUS = {
    series_draft: { label: t.adminSeriesStatusDraft, cls: 'status-pending_payment' },
    finalizing: { label: t.adminSeriesStatusGenerating, cls: 'status-in_production' },
    finalized: { label: t.adminSeriesStatusReady, cls: 'status-delivered' },
    failed: { label: t.adminSeriesStatusFailed, cls: 'status-cancelled' },
  };
  const s = STATUS[status] || { label: status, cls: 'status-pending_payment' };
  return <span className={`order-status-badge ${s.cls}`}>{s.label}</span>;
}

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const { url } = await api.postForm('/api/admin/series/upload', fd);
  return url;
}

async function viewPdf(shareToken, failMsg) {
  // Admin staff-bypass needs the bearer token, so fetch as a blob (a plain
  // window.open wouldn't send Authorization) and open the object URL.
  const res = await fetch(resolveAsset(`/api/stories/${shareToken}/book.pdf`), { headers: authHeader() });
  if (!res.ok) { alert(failMsg); return; }
  window.open(URL.createObjectURL(await res.blob()), '_blank');
}

export default function SeriesDetail() {
  const t = useT();
  const { id } = useParams();
  const [series, setSeries] = useState(null);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState({});
  const [adding, setAdding] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const data = await api.get(`/api/admin/series/${id}`);
    setSeries(data.series);
    setMembers(data.members);
  }, [id]);

  useEffect(() => { load().catch((e) => setError(e.message || t.adminSeriesLoadFailed)); }, [load, t]);

  // Poll while anything is generating so pills + covers update live.
  useEffect(() => {
    const anyGenerating = members.some((m) => m.generationStatus === 'finalizing');
    if (anyGenerating && !pollRef.current) {
      pollRef.current = setInterval(() => { load().catch(() => {}); }, 3000);
    } else if (!anyGenerating && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [members, load]);

  const applyMember = (updated) =>
    setMembers((prev) => prev.map((m) => (m.storyId === updated.storyId ? { ...m, ...updated } : m)));

  const patchMember = async (storyId, patch) => {
    const updated = await api.patch(`/api/admin/series/${id}/members/${storyId}`, patch);
    applyMember(updated);
  };

  const generate = async (storyId) => {
    setBusy((b) => ({ ...b, [storyId]: true }));
    try {
      await api.post(`/api/admin/series/${id}/members/${storyId}/generate`, {});
      await load();
    } catch (e) {
      alert(e.message || 'تعذّر بدء التوليد');
    } finally {
      setBusy((b) => ({ ...b, [storyId]: false }));
    }
  };

  const removeMember = async (storyId) => {
    if (!window.confirm(t.adminSeriesDeleteConfirm)) return;
    try {
      await api.delete(`/api/admin/series/${id}/members/${storyId}`);
      setMembers((prev) => prev.filter((m) => m.storyId !== storyId));
    } catch (e) {
      alert(e.message || 'تعذّر الحذف');
    }
  };

  const addMembers = async (count) => {
    setAdding(true);
    try {
      const { members: created } = await api.post(`/api/admin/series/${id}/members`, { count });
      setMembers((prev) => [...prev, ...created]);
    } catch (e) {
      alert(e.message || 'تعذّر الإضافة');
    } finally {
      setAdding(false);
    }
  };

  const patchSeries = async (patch) => {
    const { series: s } = await api.patch(`/api/admin/series/${id}`, patch);
    setSeries((prev) => ({ ...prev, ...s }));
  };

  if (error) {
    return (
      <div className="admin-page" dir="rtl">
        <h2>{error}</h2>
        <Link to="/admin" className="submit-btn">رجوع</Link>
      </div>
    );
  }
  if (!series) return <div className="admin-page" dir="rtl"><p>{t.adminSeriesLoading}</p></div>;

  const approvedCount = members.filter((m) => m.approved).length;

  return (
    <div className="admin-page" dir="rtl">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        {series.baseStoryId?.coverImageUrl && (
          <img src={resolveAsset(series.baseStoryId.coverImageUrl)} alt=""
            style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10 }} />
        )}
        <div style={{ flex: 1, minWidth: 220 }}>
          <input className="admin-title-input" defaultValue={series.title}
            onBlur={(e) => e.target.value !== series.title && patchSeries({ title: e.target.value })}
            style={{ fontSize: '1.2rem', fontWeight: 700 }} placeholder={t.adminSeriesTitle} />
          <p className="admin-scene-subtitle" style={{ margin: '4px 0 0' }}>
            {series.sameStoryline ? t.adminSeriesSameStory : t.adminSeriesNewStory} · {series.sameGraphics ? t.adminSeriesSameGraphics : t.adminSeriesNewGraphics} ·{' '}
            {members.length}{t.adminSeriesChildren} · {approvedCount}{t.adminSeriesApprovedCount}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 18, alignItems: 'center', margin: '14px 0', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.9rem' }}>
          {t.adminSeriesDefaultPrice}
          <input type="number" min="0" defaultValue={(series.defaultPriceCents || 0) / 100}
            onBlur={(e) => patchSeries({ defaultPriceCents: Math.round(Number(e.target.value) * 100) })}
            style={{ width: 90 }} />
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={!!series.isTest} onChange={(e) => patchSeries({ isTest: e.target.checked })} />
          {t.adminSeriesTest}
        </label>
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {members.map((m, i) => (
          <MemberCard key={m.storyId} index={i} m={m} busy={busy[m.storyId]}
            onPatch={(patch) => patchMember(m.storyId, patch)}
            onGenerate={() => generate(m.storyId)}
            onDelete={() => removeMember(m.storyId)} />
        ))}
      </div>

      <div className="admin-scene-actions" style={{ marginTop: 16, gap: 8 }}>
        <button type="button" className="ghost-btn" disabled={adding} onClick={() => addMembers(1)}>{t.adminSeriesAddChild}</button>
        <button type="button" className="ghost-btn" disabled={adding} onClick={() => addMembers(5)}>{t.adminSeriesAdd5}</button>
      </div>
    </div>
  );
}

function MemberCard({ index, m, busy, onPatch, onGenerate, onDelete }) {
  const t = useT();
  const GENDERS = [t.adminGenderBoy, t.adminGenderGirl];
  const [name, setName] = useState(m.name || '');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(false);
  useEffect(() => { setName(m.name || ''); }, [m.name]);

  const generating = m.generationStatus === 'finalizing';
  const refUrl = (m.referenceImageUrls || [])[0] || null;

  const onPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try { const url = await uploadImage(file); await onPatch({ characterImageUrl: url }); }
    catch (e) { alert(e.message || 'فشل رفع الصورة'); }
    finally { setUploadingPhoto(false); }
  };
  const onRef = async (file) => {
    if (!file) return;
    setUploadingRef(true);
    try { const url = await uploadImage(file); await onPatch({ referenceImageUrls: [url] }); }
    catch (e) { alert(e.message || 'فشل رفع الصورة'); }
    finally { setUploadingRef(false); }
  };

  const genLabel = generating ? t.adminSeriesGenerating
    : m.generationStatus === 'finalized' ? t.adminSeriesRegenerate
    : m.generationStatus === 'failed' ? t.adminSeriesRetry : t.adminSeriesCreateBook;

  return (
    <div style={{
      display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap',
      padding: '14px 16px', border: '1px solid #eee', borderRadius: 14, background: '#fff',
    }}>
      <span style={{ fontWeight: 700, color: '#9ca3af', width: 18 }}>{index + 1}</span>

      {/* Cover / photo thumbnail */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: 10, overflow: 'hidden', background: '#f3f4f6', display: 'grid', placeItems: 'center' }}>
          {m.coverImageUrl ? (
            <img src={resolveAsset(m.coverImageUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : m.characterImageUrl ? (
            <img src={resolveAsset(m.characterImageUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : <span style={{ fontSize: 22 }}>🧒</span>}
        </div>
        <div style={{ marginTop: 4 }}><StatusPill status={m.generationStatus} /></div>
      </div>

      {/* Editable fields */}
      <div style={{ flex: '1 1 320px', display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            onBlur={() => name !== m.name && onPatch({ name })}
            placeholder={t.adminSeriesChildName} style={{ flex: '1 1 160px', padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
          <select value={m.gender || t.adminGenderBoy} onChange={(e) => onPatch({ gender: e.target.value })}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <input type="number" min="1" max="14" defaultValue={m.age}
            onBlur={(e) => String(e.target.value) !== String(m.age) && onPatch({ age: e.target.value })}
            placeholder={t.adminSeriesChildAge} style={{ width: 70, padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem' }}>
            ₪
            <input type="number" min="0" defaultValue={(m.seriesPriceCents || 0) / 100}
              onBlur={(e) => {
                const cents = Math.round(Number(e.target.value) * 100);
                if (cents !== (m.seriesPriceCents || 0)) onPatch({ seriesPriceCents: cents });
              }}
              style={{ width: 72, padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }} />
          </label>
        </div>

        {/* Uploads */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.82rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {t.adminSeriesChildPhoto}
            {m.characterImageUrl && <img src={resolveAsset(m.characterImageUrl)} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} />}
            {uploadingPhoto ? <span>{t.adminSeriesUploading}</span> : (
              <label className="admin-link-btn" style={{ cursor: 'pointer' }}>
                {m.characterImageUrl ? t.adminSeriesChange : t.adminSeriesUpload}
                <input type="file" accept="image/*" hidden onChange={(e) => onPhoto(e.target.files?.[0])} />
              </label>
            )}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {t.adminSeriesRefPhoto}
            {refUrl && <img src={resolveAsset(refUrl)} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} />}
            {uploadingRef ? <span>{t.adminSeriesUploading}</span> : (
              <label className="admin-link-btn" style={{ cursor: 'pointer' }}>
                {refUrl ? t.adminSeriesChange : t.adminSeriesUpload}
                <input type="file" accept="image/*" hidden onChange={(e) => onRef(e.target.files?.[0])} />
              </label>
            )}
          </span>
        </div>

        {m.generationStatus === 'failed' && m.seriesError && (
          <div style={{ fontSize: '0.75rem', color: '#dc2626' }}>{m.seriesError}</div>
        )}
        {!m.characterImageUrl && (
          <div style={{ fontSize: '0.75rem', color: '#b45309' }}>{t.adminSeriesUploadPhotoFirst}</div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch', minWidth: 150 }}>
        <button type="button" className="submit-btn" style={{ padding: '8px 12px', fontSize: '0.82rem' }}
          disabled={busy || generating || !m.characterImageUrl} onClick={onGenerate}>
          {genLabel}
        </button>
        <Link to={`/admin/stories/${m.storyId}`} className="ghost-btn" style={{ padding: '8px 12px', fontSize: '0.82rem', textAlign: 'center' }}>
          {t.adminSeriesEditScenes}
        </Link>
        {m.generationStatus === 'finalized' && (
          <button type="button" className="ghost-btn" style={{ padding: '8px 12px', fontSize: '0.82rem' }} onClick={() => viewPdf(m.shareToken, t.adminSeriesPdfFailed)}>
            {t.adminSeriesViewPdf}
          </button>
        )}
        <button type="button"
          className={m.approved ? 'approval-badge approval-badge-approved' : 'approval-badge approval-badge-pending'}
          onClick={() => onPatch({ approved: !m.approved })} style={{ cursor: 'pointer', border: 'none' }}>
          {m.approved ? t.adminSeriesApprovedBadge : t.adminSeriesApprove}
        </button>
        <button type="button" className="admin-link-btn" style={{ color: '#dc2626' }} disabled={generating} onClick={onDelete}>
          {t.adminSeriesDeleteChild}
        </button>
      </div>
    </div>
  );
}
